import {
  Box,
  Flex,
  HStack,
  IconButton,
  IconButtonProps,
  Input,
  InputProps,
} from '@chakra-ui/react';

import { AddIcon, MinusIcon } from './Icons';

/**
 * Normalizes an `<Input />` value prop so we never render the literal string "NaN" to users.
 *
 * This is a defensive UI-only guard. The primary protection should come from upstream state/hooks
 * (for example `useStockNumberInput`), but this prevents accidental `NaN` leakage from other callers.
 */
const normalizeNaNInputValue = (
  value: InputProps['value']
): InputProps['value'] => {
  if (typeof value === 'number' && Number.isNaN(value)) return '';
  if (typeof value === 'string' && value.trim().toLowerCase() === 'nan')
    return '';
  return value;
};

interface QuantityControlProps {
  isLoading?: boolean;
  inputDisabled?: boolean;
  incDisabled?: boolean;
  decDisabled?: boolean;
  hasFractionalStep: boolean;
  inputProps: Partial<InputProps>;
  incProps: Partial<IconButtonProps>;
  decProps: Partial<IconButtonProps>;
  variant?: 'default' | 'add-to-cart' | 'cart';
}

export const QuantityControl = ({
  isLoading,
  inputDisabled,
  incDisabled,
  decDisabled,
  hasFractionalStep,
  inputProps,
  incProps,
  decProps,
  variant = 'default',
}: QuantityControlProps) => {
  const isAddToCart = variant === 'add-to-cart';
  const isCart = variant === 'cart';

  const normalizedValue =
    inputProps.value === undefined
      ? undefined
      : normalizeNaNInputValue(inputProps.value);

  const normalizedInputProps: Partial<InputProps> = {
    ...inputProps,
    ...(normalizedValue !== undefined ? { value: normalizedValue } : {}),
  };

  return (
    <HStack
      mt={isAddToCart ? 0 : { base: '0', lg: '2' }}
      borderWidth="1px"
      borderColor={isAddToCart ? 'blue.500' : 'gray.33'}
      align="center"
      rounded={isAddToCart ? 'lg' : '4px'}
      height={isAddToCart ? '46px' : { base: '28px', lg: '32px' }}
      w={isCart ? '122px' : 'full'}
      p={isAddToCart ? 2 : 1}
      ml={isAddToCart ? 0 : { base: '0', lg: 2 }}
      spacing={0}
    >
      <Flex
        justify="flex-start"
        align="center"
        w={{ base: 'fit-content', lg: 'auto' }}
        flex={isAddToCart ? 1 : 'none'}
      >
        <IconButton
          {...decProps}
          aria-label="min"
          name="min"
          icon={
            <MinusIcon
              boxSize={isAddToCart ? '20px' : { base: '4' }}
              color="#FF0000"
            />
          }
          w={isAddToCart ? '32px' : '20px'}
          h={isAddToCart ? '32px' : '20px'}
          minW={isAddToCart ? '32px' : '20px'}
          bg={decDisabled ? '#D9D9D9' : 'gray.200'}
          color="white"
          p="2px"
          _hover={{
            bgColor: '#b3b3b3',
          }}
          isDisabled={Boolean(decDisabled)}
          isLoading={isLoading}
          onBlur={(e) => e.preventDefault()}
          borderRadius={isAddToCart ? 'md' : '2px'}
        />
      </Flex>
      <Box
        flex={isAddToCart ? 'none' : '1'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minWidth="0"
        px={1}
        width={isAddToCart ? '80px' : 'auto'}
      >
        <Input
          fontWeight="semibold"
          textAlign="center"
          variant="unstyled"
          isDisabled={Boolean(inputDisabled)}
          fontSize={{ base: '14px', md: '16px' }}
          autoComplete="off"
          className="input-total-order"
          width="100%"
          inputMode={hasFractionalStep ? 'decimal' : 'numeric'}
          {...normalizedInputProps}
        />
      </Box>
      <Flex
        justify="flex-end"
        align="center"
        w={{ base: 'fit-content', lg: 'auto' }}
        flex={isAddToCart ? 1 : 'none'}
      >
        <IconButton
          {...incProps}
          aria-label="plus"
          name="plus"
          icon={
            <AddIcon
              boxSize={isAddToCart ? '20px' : { base: '4' }}
              color="#0000A3"
            />
          }
          w={isAddToCart ? '32px' : '20px'}
          h={isAddToCart ? '32px' : '20px'}
          minW={isAddToCart ? '32px' : '20px'}
          border={isAddToCart ? 'none' : '1px'}
          bg={incDisabled ? '#D9D9D9' : 'gray.200'}
          color="white"
          _hover={{
            bgColor: '#b3b3b3',
          }}
          isLoading={isLoading}
          isDisabled={Boolean(incDisabled)}
          onBlur={(e) => e.preventDefault()}
          borderRadius={isAddToCart ? 'md' : '2px'}
          p="2px"
        />
      </Flex>
    </HStack>
  );
};
